/**
 * Beneficial Owner Letters — Appendix A/B/C (M32109)
 * Notice(s.60C, M32108) 와 별도 회신 양식 패키지.
 *
 * Corporate / Individual 동시 선택 시 필드가 서로 덮이지 않도록
 * BA02~BA05 는 문서 코드 prefix (ba02_, ba03_, …) 를 둡니다.
 * Word/저장 시 adaptFormDataForDoc 이 canonical 키(bo_*, sm_*, …)로 풀어줍니다.
 */
import nationalitiesJson from '@/features/startTask/preset_task/my/components/shared/data/MBRS/nationalities.json';

export type BoAppendixFieldType = 'text' | 'date' | 'entity' | 'person' | 'address' | 'combo';

export type BoAppendixDocCode = 'BA01' | 'BA02' | 'BA03' | 'BA04' | 'BA05';

export type BoAppendixDocKey =
  | 'appendix_a'
  | 'appendix_b_corporate'
  | 'appendix_b_individual'
  | 'appendix_c_corporate'
  | 'appendix_c_individual';

export interface BoAppendixDocMeta {
  docCode: BoAppendixDocCode;
  docKey: BoAppendixDocKey;
  label: string;
  icon: string;
  keys: string[];
  designationKey: string;
  targetTitle: string;
  /** values 키 prefix. BA01 은 '' */
  keyPrefix: string;
}

const BO_PARTICULARS = [
  'bo_full_name',
  'bo_residential_address',
  'bo_business_address',
  'bo_email',
  'bo_nationality',
  'bo_dob',
  'bo_race',
  'bo_gender',
  'bo_id_no',
  'bo_contact_no',
  'bo_position',
  'bo_date_became',
];

const BO_PCT = [
  'pct_direct_shares',
  'pct_direct_voting',
  'pct_indirect_shares',
  'pct_indirect_voting',
  'pct_combined_shares',
  'pct_combined_voting',
];

const SM_PARTICULARS = [
  'sm_full_name',
  'sm_office_address',
  'sm_email',
  'sm_residential_address',
  'sm_phone',
  'sm_nationality',
  'sm_dob',
  'sm_race',
  'sm_gender',
  'sm_id_no',
  'sm_position',
  'sm_appointment_date',
];

/** 문서별 values 키 prefix */
export function docKeyPrefix(docCode: BoAppendixDocCode): string {
  if (docCode === 'BA01') return '';
  return `${docCode.toLowerCase()}_`;
}

export function prefixedKeys(docCode: BoAppendixDocCode, bases: string[]): string[] {
  const p = docKeyPrefix(docCode);
  return bases.map((k) => (p ? `${p}${k}` : k));
}

/** ba02_bo_full_name → bo_full_name */
export function stripDocKeyPrefix(key: string): string {
  return key.replace(/^ba0[2-5]_/i, '');
}

/** ba02_bo_full_name → BA02 */
export function docCodeFromFieldKey(key: string): BoAppendixDocCode | null {
  const m = key.match(/^(ba0[2-5])_/i);
  if (m) return m[1].toUpperCase() as BoAppendixDocCode;
  if (/^(bo_|pct_)/.test(key) || key === 'signer_designation_BA01') return 'BA01';
  return null;
}

function withPrefix(docCode: BoAppendixDocCode, bases: string[]): string[] {
  return prefixedKeys(docCode, bases);
}

export const BO_APPENDIX_DOCS: Record<BoAppendixDocCode, BoAppendixDocMeta> = {
  BA01: {
    docCode: 'BA01',
    docKey: 'appendix_a',
    label: 'Appendix A — Particulars of Beneficial Owner',
    icon: '📝',
    designationKey: 'signer_designation_BA01',
    targetTitle: 'Beneficial Owner',
    keyPrefix: '',
    keys: [...BO_PARTICULARS, ...BO_PCT, 'signer_designation_BA01'],
  },
  BA02: {
    docCode: 'BA02',
    docKey: 'appendix_b_corporate',
    label: 'Appendix B — Others as BO (Corporate)',
    icon: '🏢',
    designationKey: 'signer_designation_BA02',
    targetTitle: 'Authorised Signatory',
    keyPrefix: 'ba02_',
    keys: [
      ...withPrefix('BA02', BO_PARTICULARS),
      ...withPrefix('BA02', BO_PCT),
      'ba02_member_name',
      'ba02_member_registration_no',
      'signer_designation_BA02',
    ],
  },
  BA03: {
    docCode: 'BA03',
    docKey: 'appendix_b_individual',
    label: 'Appendix B — Others as BO (Individual)',
    icon: '🙍',
    designationKey: 'signer_designation_BA03',
    targetTitle: 'Member',
    keyPrefix: 'ba03_',
    keys: [
      ...withPrefix('BA03', BO_PARTICULARS),
      ...withPrefix('BA03', BO_PCT),
      'signer_designation_BA03',
    ],
  },
  BA04: {
    docCode: 'BA04',
    docKey: 'appendix_c_corporate',
    label: 'Appendix C — Senior Management (Corporate)',
    icon: '👔',
    designationKey: 'signer_designation_BA04',
    targetTitle: 'Director',
    keyPrefix: 'ba04_',
    keys: [
      ...withPrefix('BA04', SM_PARTICULARS),
      'ba04_member_name',
      'ba04_member_registration_no',
      'ba04_sm_reason_id',
      'signer_designation_BA04',
    ],
  },
  BA05: {
    docCode: 'BA05',
    docKey: 'appendix_c_individual',
    label: 'Appendix C — Senior Management (Individual)',
    icon: '👤',
    designationKey: 'signer_designation_BA05',
    targetTitle: 'Senior Management',
    keyPrefix: 'ba05_',
    keys: [
      ...withPrefix('BA05', SM_PARTICULARS),
      'ba05_sm_reason',
      'ba05_sm_reason_id',
      'signer_designation_BA05',
    ],
  },
};

export const BO_APPENDIX_DOC_CODES = Object.keys(BO_APPENDIX_DOCS) as BoAppendixDocCode[];

export const DOC_KEY_TO_CODE: Record<BoAppendixDocKey, BoAppendixDocCode> = {
  appendix_a: 'BA01',
  appendix_b_corporate: 'BA02',
  appendix_b_individual: 'BA03',
  appendix_c_corporate: 'BA04',
  appendix_c_individual: 'BA05',
};

export const CODE_TO_DOC_KEY: Record<BoAppendixDocCode, BoAppendixDocKey> = {
  BA01: 'appendix_a',
  BA02: 'appendix_b_corporate',
  BA03: 'appendix_b_individual',
  BA04: 'appendix_c_corporate',
  BA05: 'appendix_c_individual',
};

const OVERRIDES: Record<string, string> = {
  bo_full_name: 'BO Full Name',
  bo_residential_address: 'BO Residential Address',
  bo_business_address: 'BO Business Address',
  bo_email: 'BO Email',
  bo_nationality: 'BO Nationality',
  bo_dob: 'BO Date of Birth',
  bo_race: 'BO Race',
  bo_gender: 'BO Gender',
  bo_id_no: 'BO NRIC / Passport',
  bo_contact_no: 'BO Contact No.',
  bo_position: 'BO Position',
  bo_date_became: 'Date Became BO',
  pct_direct_shares: 'Direct shares %',
  pct_direct_voting: 'Direct voting %',
  pct_indirect_shares: 'Indirect shares %',
  pct_indirect_voting: 'Indirect voting %',
  pct_combined_shares: 'Combined shares %',
  pct_combined_voting: 'Combined voting %',
  sm_full_name: 'SM Full Name',
  sm_office_address: 'SM Office Address',
  sm_email: 'SM Email',
  sm_residential_address: 'SM Residential Address',
  sm_phone: 'SM Phone',
  sm_nationality: 'SM Nationality',
  sm_dob: 'SM Date of Birth',
  sm_race: 'SM Race',
  sm_gender: 'SM Gender',
  sm_id_no: 'SM NRIC / Passport',
  sm_position: 'SM Position',
  sm_appointment_date: 'SM Appointment Date',
  sm_reason: 'Reason for naming senior management',
  sm_reason_id: 'SM Reason Id',
  member_name: 'Corporate Member Name',
  member_registration_no: 'Member Registration No.',
  signer_designation_BA01: 'Signer Designation',
  signer_designation_BA02: 'Signer Designation',
  signer_designation_BA03: 'Signer Designation',
  signer_designation_BA04: 'Signer Designation',
  signer_designation_BA05: 'Signer Designation',
  signer_designation: 'Signer Designation',
};

export function labelize(key: string): string {
  const base = stripDocKeyPrefix(key);
  if (OVERRIDES[base]) return OVERRIDES[base];
  if (OVERRIDES[key]) return OVERRIDES[key];
  return base.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function typeOf(key: string): BoAppendixFieldType {
  const k = stripDocKeyPrefix(key);
  if (/_date$|_dob$|date_became|appointment_date/.test(k)) return 'date';
  if (/_address$/.test(k)) return 'address';
  if (/^(bo_full_name|sm_full_name|member_name)$/.test(k)) return 'person';
  if (/_(race|gender|nationality)$/.test(k)) return 'combo';
  return 'text';
}

/** CONTACT MY (EntityController / contact.Race) 와 동일 */
export type BoAppendixComboOpt = { value: string; label: string };

export const BO_GENDER_OPTIONS: BoAppendixComboOpt[] = [
  { value: 'MALE', label: 'MALE' },
  { value: 'FEMALE', label: 'FEMALE' },
];

/** CONTACT MY Race 콤보 — EntityControllerSection MY_RACE_OPTIONS */
export const BO_RACE_OPTIONS: BoAppendixComboOpt[] = [
  'Bidayuh',
  'Chinese',
  'Dusun',
  'Foreigner',
  'Iban',
  'Indian',
  'Kadazan',
  'Malay',
  'Other Race',
].map((v) => ({ value: v, label: v }));

/** CDD / MBRS nationality 콤보와 동일 — value=label (대문자 국가명) */
export const BO_NATIONALITY_OPTIONS: BoAppendixComboOpt[] = (
  nationalitiesJson as { value: string; label: string }[]
)
  .map((c) => {
    const v = String(c.label || c.value || '').trim().toUpperCase();
    return { value: v, label: v };
  })
  .filter((c) => !!c.value);

export type BoAppendixComboSource = 'race' | 'gender' | 'nationality';

export function comboSourceOf(key: string): BoAppendixComboSource | null {
  const k = stripDocKeyPrefix(key);
  if (/_race$/.test(k)) return 'race';
  if (/_gender$/.test(k)) return 'gender';
  if (/_nationality$/.test(k)) return 'nationality';
  return null;
}

export function comboOptionsOf(key: string): BoAppendixComboOpt[] {
  const src = comboSourceOf(key);
  if (src === 'race') return BO_RACE_OPTIONS;
  if (src === 'gender') return BO_GENDER_OPTIONS;
  if (src === 'nationality') return BO_NATIONALITY_OPTIONS;
  return [];
}

/** 자동채움/외부값 → CONTACT MY Race 콤보 value */
export function resolveBoRace(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const u = s.toUpperCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  const hit = BO_RACE_OPTIONS.find((o) => {
    const ov = o.value.toUpperCase();
    const ol = o.label.toUpperCase();
    return ov === u || ol === u || ov.replace(/\s+/g, '') === u.replace(/\s+/g, '');
  });
  if (hit) return hit.value;
  if (/OTHER/.test(u)) return 'Other Race';
  return '';
}

/** 자동채움/외부값 → CONTACT MY Gender 콤보 value */
export function resolveBoGender(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const u = s.toUpperCase();
  if (u === 'MALE' || u === 'M' || u === '1' || u === 'TRUE') return 'MALE';
  if (u === 'FEMALE' || u === 'F' || u === '0' || u === 'FALSE') return 'FEMALE';
  return '';
}

/** Person Lookup / B15 코드 → nationality 콤보 value (라벨) */
export function resolveBoNationality(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const u = s.toUpperCase().replace(/\s+/g, ' ').trim();
  const byValue = BO_NATIONALITY_OPTIONS.find(
    (o) => o.value === u || o.label.toUpperCase() === u,
  );
  if (byValue) return byValue.value;
  // 부분 일치 (예: Malaysia → MALAYSIA)
  const partial = BO_NATIONALITY_OPTIONS.find(
    (o) => o.value.includes(u) || u.includes(o.value),
  );
  return partial ? partial.value : '';
}

export function isOptional(key: string): boolean {
  const k = stripDocKeyPrefix(key);
  return key.startsWith('signer_designation')
    || k.startsWith('pct_')
    || k === 'bo_business_address'
    || k === 'bo_email'
    || k === 'bo_position'
    || k === 'sm_email'
    || k === 'sm_reason'
    || k === 'member_registration_no';
}

/**
 * 문서별 prefix 키 → Word/canonical 키로 풀어 저장.
 * 예: ba02_bo_full_name → bo_full_name (해당 문서 formData 안에서만)
 */
export function adaptFormDataForDoc(
  docCode: BoAppendixDocCode,
  base: Record<string, any>,
): Record<string, any> {
  const fd = { ...base };
  const meta = BO_APPENDIX_DOCS[docCode];
  const prefix = meta.keyPrefix;
  if (prefix) {
    for (const [k, v] of Object.entries(base)) {
      if (!k.startsWith(prefix)) continue;
      const canon = k.slice(prefix.length);
      fd[canon] = v;
    }
  }
  const desig = fd[meta.designationKey] || meta.targetTitle;
  if (desig) fd.signer_designation = desig;
  return fd;
}

export { BO_PARTICULARS, BO_PCT, SM_PARTICULARS };
